import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";

/**
 * Lightweight combobox for picking an Italian city.
 * - Click trigger → opens panel with search input + filtered list.
 * - Renders at most MAX_VISIBLE results to keep DOM tiny (avoids Chrome crashes
 *   we hit with Radix Select for provinces with 200-300+ cities).
 * - Pure inline UI: no portal, no virtualization library, no Radix.
 */

const MAX_VISIBLE = 60;

export default function CityCombobox({
  value,
  onChange,
  options,
  placeholder = "Seleziona...",
  disabled = false,
  disabledPlaceholder = "Seleziona prima la provincia",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // Focus search input when panel opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const list = options || [];
    if (!query.trim()) return list.slice(0, MAX_VISIBLE);
    const q = query.toLowerCase().trim();
    const matches = [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].toLowerCase().includes(q)) {
        matches.push(list[i]);
        if (matches.length >= MAX_VISIBLE) break;
      }
    }
    return matches;
  }, [options, query]);

  const totalMatches = useMemo(() => {
    if (!query.trim()) return (options || []).length;
    const q = query.toLowerCase().trim();
    return (options || []).filter(o => o.toLowerCase().includes(q)).length;
  }, [options, query]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(v => !v)}
        data-testid={testid}
        className={`w-full flex items-center justify-between gap-2 h-9 px-3 py-1 text-sm rounded-md border bg-white shadow-sm transition-colors ${
          disabled
            ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
            : "border-gray-300 text-gray-900 hover:border-gray-400"
        }`}
      >
        <span className={`truncate text-left flex-1 ${!value ? "text-gray-400" : ""}`}>
          {disabled ? disabledPlaceholder : (value || placeholder)}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca..."
                className="w-full h-8 pl-2 pr-7 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                autoComplete="off"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  tabIndex={-1}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400 text-center">Nessun risultato</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt);
                      setOpen(false);
                      setQuery("");
                    }}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-teal-50 transition-colors ${
                      value === opt ? "bg-teal-100 text-teal-900 font-medium" : "text-gray-800"
                    }`}
                  >
                    {opt}
                  </button>
                </li>
              ))
            )}
          </ul>
          {totalMatches > filtered.length && (
            <div className="px-3 py-1.5 text-[10px] text-gray-500 bg-gray-50 border-t border-gray-100">
              Mostrate {filtered.length} di {totalMatches} — digita per filtrare
            </div>
          )}
        </div>
      )}
    </div>
  );
}
